// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Work out the dimensions to trace at. If `maxDim` is 0 (or already larger than
 * the image) the source dimensions are used unchanged. Otherwise the longest
 * side is scaled down to `maxDim`, preserving aspect ratio, never up-scaling.
 */
export function computeTraceSize(
  srcW: number,
  srcH: number,
  maxDim: number,
): { width: number; height: number; scaled: boolean } {
  const w = Math.max(1, Math.round(srcW));
  const h = Math.max(1, Math.round(srcH));
  if (!maxDim || maxDim <= 0) return { width: w, height: h, scaled: false };
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { width: w, height: h, scaled: false };
  const k = maxDim / longest;
  return {
    width: Math.max(1, Math.round(w * k)),
    height: Math.max(1, Math.round(h * k)),
    scaled: true,
  };
}

/** Human-readable byte size. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/** Formats we accept for import. */
export const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/avif',
];

export function isAcceptedImage(file: { type: string; name?: string }): boolean {
  if (file.type && ACCEPTED_TYPES.includes(file.type)) return true;
  // Some browsers omit the type; fall back to extension.
  const name = (file.name ?? '').toLowerCase();
  return /\.(png|jpe?g|webp|gif|bmp|avif)$/.test(name);
}
